import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import "./style.scss";
import axios from "axios";
import DialogMessage from "../../DialogMessage";
import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { darken } from "@mui/system";

const RegisterSponsorPage = () => {
  const navigate = useNavigate();
  const { conferenceId } = useParams();

  const API_BASE = "https://eventscons.com/eventbackendnew/api";

  const [companyName, setCompanyName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [salutation, setSalutation] = useState("");
  const [heardFrom, setHeardFrom] = useState(""); // optional, not sent to API unless you want
  const [countryOfResidence, setCountryOfResidence] = useState("UAE");
  const [phone, setPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [email, setEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [nationality, setNationality] = useState("India");

  // ✅ IMPORTANT: password from form
  const [password, setPassword] = useState("");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [registering, setRegistering] = useState(false);

  // ✅ countries dropdown state
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [formData, setFormData] = useState({
    accept_cancellation_policy: false,
  });

  const [error, setError] = useState({
    companyName: "",
    contactPerson: "",
    phone: "",
    whatsApp: "",
    email: "",
    companyAddress: "",
    password: "",
    specialization: "",
    countryOfResidence: "",
    nationality: "",
    salutation: "",
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const res = await axios.get(`${API_BASE}/countries`, {
          headers: { Accept: "application/json" },
        });
        const list = res.data?.data || [];
        setCountries(list);

        if (list.length && !list.some((c) => c.name === countryOfResidence)) {
          setCountryOfResidence(list[0].name);
        }
        if (list.length && !list.some((c) => c.name === nationality)) {
          setNationality(list[0].name);
        }
      } catch (e) {
        console.error("Countries API error:", e);
        toast.error("Failed to load countries.");
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validatePassword = (val) => {
    // at least 8, 1 uppercase, 1 number, 1 special
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return regex.test(val);
  };

  const handleSubmit = async () => {
    if (!formData.accept_cancellation_policy) {
      toast.error("Please accept the cancellation and refund policy.");
      return;
    }

    // ✅ payload exactly as your API sample expects
    const payload = {
      name: contactPerson,
      title: salutation,
      email,
      password,
      registration_type: "sponsor",
      phone_number: phone, // sample shows without + (keep same)
      whatsapp_number: whatsApp, // sample shows without + (keep same)
      specialization,
      nationality,
      country_of_residence: countryOfResidence,
      isAdmin: true, // sample shows true
      company_name: companyName,
      contact_person: contactPerson,
      company_address: companyAddress,
      conference_id: Number(conferenceId),
    };

    try {
      setRegistering(true);

      const res = await axios.post(`${API_BASE}/sponsorusers`, payload, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });

      if (res.data?.user) {
        toast.success("Registration successful!");
        setIsDialogOpen(true);
      } else {
        toast.error("Registration failed. Please try again.");
        console.error("Unexpected response:", res.data);
      }
    } catch (err) {
      console.error("Sponsor register error:", err.response?.data || err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Registration failed. Please check your details."
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleRegister = (e) => {
    e.preventDefault();

    let errorCompanyName = "";
    let errorContactPerson = "";
    let errorPhone = "";
    let errorWhatsApp = "";
    let errorEmail = "";
    let errorCompanyAddress = "";
    let errorPassword = "";
    let errorSpecialization = "";
    let errorCountry = "";
    let errorNationality = "";
    let errorSalutation = "";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!salutation) errorSalutation = "Please select salutation.";
    if (!companyName) errorCompanyName = "Please enter the company name.";
    if (!contactPerson) errorContactPerson = "Please enter the contact person.";
    if (!phone) errorPhone = "Please enter the phone number.";
    if (!whatsApp) errorWhatsApp = "Please enter the WhatsApp number.";
    if (!email) errorEmail = "Please enter the email.";
    else if (!emailRegex.test(email)) errorEmail = "Please enter a valid email.";
    if (!companyAddress) errorCompanyAddress = "Please enter the company address.";
    if (!specialization) errorSpecialization = "Please enter specialization.";
    if (!countryOfResidence) errorCountry = "Please select country of residence.";
    if (!nationality) errorNationality = "Please select nationality.";

    if (!password) {
      errorPassword = "Please enter a password.";
    } else if (!validatePassword(password)) {
      errorPassword =
        "Password must be at least 8 characters, include one capital letter, one number, and one special character.";
    }

    setError({
      companyName: errorCompanyName,
      contactPerson: errorContactPerson,
      phone: errorPhone,
      whatsApp: errorWhatsApp,
      email: errorEmail,
      companyAddress: errorCompanyAddress,
      password: errorPassword,
      specialization: errorSpecialization,
      countryOfResidence: errorCountry,
      nationality: errorNationality,
      salutation: errorSalutation,
    });

    if (
      salutation &&
      companyName &&
      contactPerson &&
      phone &&
      whatsApp &&
      emailRegex.test(email) &&
      companyAddress &&
      specialization &&
      countryOfResidence &&
      nationality &&
      validatePassword(password)
    ) {
      handleSubmit();
    }
  };

  return (
    <Container
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20vh",
      }}
    >
      <DialogMessage
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        message="Thank you for applying to Sponsor the conference. You will be contacted directly by the organizing company via email."
      />

      <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Grid xs={12} sm={12} md={6} lg={6} xl={6}>
          <form onSubmit={handleRegister}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <Typography sx={{ padding: "10px", color: "#c62828" }} variant="h4">
                  Register Company
                </Typography>
                <Button
                  sx={{
                    color: "white",
                    backgroundColor: "#9B1321",
                    "&:hover": { backgroundColor: darken("#dc143c", 0.2) },
                  }}
                  onClick={() => navigate("/login")}
                >
                  Login
                </Button>
              </Grid>

              {/* Salutation */}
              <Grid item xs={12}>
                <TextField
                  select
                  label="Salutation"
                  value={salutation}
                  onChange={(e) => setSalutation(e.target.value)}
                  fullWidth
                  error={!!error.salutation}
                  helperText={error.salutation}
                >
                  <MenuItem value="Mr.">Mr.</MenuItem>
                  <MenuItem value="Mrs.">Mrs.</MenuItem>
                  <MenuItem value="Ms.">Ms.</MenuItem>
                  <MenuItem value="Dr.">Dr.</MenuItem>
                  <MenuItem value="Prof.">Prof.</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Company Name"
                  error={!!error.companyName}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  helperText={error.companyName}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Contact Person"
                  error={!!error.contactPerson}
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  helperText={error.contactPerson}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Specialization"
                  error={!!error.specialization}
                  value={specialization}
                  onChange={(e) => setSpecialization(e.target.value)}
                  helperText={error.specialization}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email"
                  error={!!error.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  helperText={error.email}
                  fullWidth
                />
              </Grid>

              {/* ✅ Password visible (form se value aayegi) */}
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type="password"
                  error={!!error.password}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText={error.password}
                  fullWidth
                  placeholder="securePass123"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel shrink style={{ marginBottom: "8px" }}>
                    WhatsApp Number
                  </InputLabel>
                  <PhoneInput
                    country={"jo"}
                    value={whatsApp}
                    onChange={setWhatsApp}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderColor: error.whatsApp ? "red" : "#c4c4c4",
                      borderRadius: "4px",
                    }}
                  />
                  {error.whatsApp && (
                    <FormHelperText style={{ color: "red" }}>{error.whatsApp}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel shrink style={{ marginBottom: "8px" }}>
                    Phone Number
                  </InputLabel>
                  <PhoneInput
                    country={"jo"}
                    value={phone}
                    onChange={setPhone}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderColor: error.phone ? "red" : "#c4c4c4",
                      borderRadius: "4px",
                    }}
                  />
                  {error.phone && (
                    <FormHelperText style={{ color: "red" }}>{error.phone}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Company Address"
                  error={!!error.companyAddress}
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  helperText={error.companyAddress}
                  fullWidth
                />
              </Grid>

              {/* Country of Residence dropdown */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!error.countryOfResidence}>
                  <InputLabel id="country-residence-label">Country of Residence</InputLabel>
                  <Select
                    labelId="country-residence-label"
                    label="Country of Residence"
                    value={countryOfResidence}
                    onChange={(e) => setCountryOfResidence(e.target.value)}
                    disabled={loadingCountries}
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.id} value={c.name}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={c.flag_url}
                            alt={`${c.name} flag`}
                            width={22}
                            height={16}
                            style={{ borderRadius: 2, objectFit: "cover" }}
                            loading="lazy"
                          />
                          <span>{c.name}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                  {error.countryOfResidence && (
                    <FormHelperText>{error.countryOfResidence}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Nationality dropdown */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!error.nationality}>
                  <InputLabel id="nationality-label">Nationality</InputLabel>
                  <Select
                    labelId="nationality-label"
                    label="Nationality"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    disabled={loadingCountries}
                  >
                    {countries.map((c) => (
                      <MenuItem key={`nat-${c.id}`} value={c.name}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={c.flag_url}
                            alt={`${c.name} flag`}
                            width={22}
                            height={16}
                            style={{ borderRadius: 2, objectFit: "cover" }}
                            loading="lazy"
                          />
                          <span>{c.name}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                  {error.nationality && <FormHelperText>{error.nationality}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Cancellation policy checkbox */}
              <Grid item xs={12}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input
                    type="checkbox"
                    name="accept_cancellation_policy"
                    checked={formData.accept_cancellation_policy}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        accept_cancellation_policy: e.target.checked,
                      }))
                    }
                    required
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                    I agree to the{" "}
                    <a
                      href="/cancellation-and-refund-policy"
                      style={{ fontWeight: "700", textDecoration: "underline" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cancellation and Refund Policy
                    </a>
                    . I confirm that I have read and accepted the terms in case of cancellation or
                    no-show.
                  </span>
                </div>
              </Grid>

              <Grid item xs={12} textAlign="right">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: "#9B1321",
                    "&:hover": { backgroundColor: darken("#dc143c", 0.2) },
                    color: "#fff",
                    minWidth: 160,
                  }}
                  disabled={registering}
                >
                  {registering ? "Registering..." : "Register"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RegisterSponsorPage;
